import React from "react";
import { Truck, Gift, Check } from "lucide-react";
import { useSiteSettings } from "../lib/site-settings";

/**
 * Unified perks ladder for the cart + checkout side panel.
 *
 * Shows two thresholds the customer can unlock:
 *   1. Free UK shipping  (free_shipping_threshold, currently £100)
 *   2. Free GHK-Cu 50mg  (gift_promotion_min_subtotal, currently £150)
 *
 * Each step renders as a row with an icon, status text, and a progress
 * bar that fills as the customer adds more to the basket. When a step is
 * unlocked, the row flips to a green confirmation state.
 *
 * Props
 *   subtotal — pre-discount subtotal in GBP. Both thresholds use this
 *              value on the cart page. The checkout page passes the
 *              post-discount net subtotal so the indicator stays
 *              consistent with the actual eligibility rule.
 */
export default function PerksLadder({ subtotal }) {
  const {
    free_shipping_threshold,
    gift_promotion_enabled,
    gift_promotion_min_subtotal,
  } = useSiteSettings();

  const tiers = [];

  // Tier 1 — free shipping
  if (Number(free_shipping_threshold) > 0) {
    const min = Number(free_shipping_threshold);
    tiers.push({
      key: "free-shipping",
      icon: Truck,
      min,
      unlockedLabel: "Free UK shipping unlocked",
      lockedLabel: (rem) => `Spend £${rem.toFixed(2)} more for free UK shipping`,
      unlockedNote: null,
    });
  }

  // Tier 2 — free gift. Always shown when the promo is active, even if
  // the customer is already buying GHK-Cu — the free vial stacks on top
  // of any paid GHK-Cu in their basket.
  if (gift_promotion_enabled && Number(gift_promotion_min_subtotal) > 0) {
    const min = Number(gift_promotion_min_subtotal);
    tiers.push({
      key: "free-gift",
      icon: Gift,
      min,
      unlockedLabel: "Free GHK-Cu 50mg unlocked",
      lockedLabel: (rem) => `Spend £${rem.toFixed(2)} more for a free GHK-Cu 50mg`,
      unlockedNote: "Added automatically at checkout — no code required.",
    });
  }

  if (tiers.length === 0) return null;

  return (
    <div className="mb-4 space-y-3" data-testid="perks-ladder">
      {tiers.map((tier) => {
        const unlocked = subtotal >= tier.min;
        const remaining = Math.max(0, tier.min - subtotal);
        const progress = Math.min(100, (subtotal / tier.min) * 100);
        const Icon = unlocked ? Check : tier.icon;

        return (
          <div
            key={tier.key}
            data-testid={`perks-${tier.key}-${unlocked ? "unlocked" : "progress"}`}
            className="p-3 rounded-sm border"
            style={{
              borderColor: unlocked
                ? "#A6FF00"
                : "rgba(94, 234, 212, 0.35)",
              background: unlocked
                ? "linear-gradient(90deg, rgba(166,255,0,0.10), rgba(166,255,0,0.04))"
                : "rgba(11, 18, 32, 0.6)",
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon
                size={14}
                style={{ color: unlocked ? "#A6FF00" : "#5EEAD4" }}
              />
              <span
                className="pg-mono text-[10px] uppercase tracking-[0.22em] font-semibold"
                style={{ color: unlocked ? "#A6FF00" : "#5EEAD4" }}
              >
                {unlocked ? tier.unlockedLabel : tier.lockedLabel(remaining)}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-[#1E2E44] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progress}%`,
                  background: unlocked
                    ? "linear-gradient(90deg, #5EEAD4, #A6FF00)"
                    : "linear-gradient(90deg, #00C2D6, #5EEAD4)",
                }}
              />
            </div>
            {unlocked && tier.unlockedNote && (
              <p className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8] mt-2">
                {tier.unlockedNote}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
